import { detect } from "detect-browser";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@/modules/notify/notify";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { useGetHandbookLinkQuery } from "../../hooks/useGetHandbookLinkQuery";

import { createHandbookItemStyles } from "./createHandbookItemStyles";
import { ReactComponent as DownloadIcon } from "./download-icon.svg";

type HandbookItemProps = {
  handbookId: string;
  handbookName: string;
};

const translationNamespace = "pages/HandbooksPage";

export const HandbookItem: React.FC<HandbookItemProps> = ({
  handbookId,
  handbookName,
}) => {
  const {
    isFetching: isHandbookLinkFetching,
    refetch: refetchHandbookLink,
  } = useGetHandbookLinkQuery(handbookId);
  const { t } = useTranslation(translationNamespace);
  const browser = detect();

  const iosBrowsers = ["ios", "ios-webview"];
  const windowTarget =
    browser && iosBrowsers.includes(browser.name) ? "_blank" : "_self";

  const handleHandbookDownload = useCallback(async () => {
    const newTabInstance = window.open("", windowTarget);
    const queryResult = await refetchHandbookLink();
    if (newTabInstance && queryResult.data?.url) {
      newTabInstance.location.href = queryResult.data.url;
    } else {
      notify.alert.error(t("Something went wrong..."));
    }
  }, [refetchHandbookLink, t, windowTarget]);

  const classes = createHandbookItemStyles(useTheme());
  return (
    <li className={classes.root}>
      <Typography className={classes.name} variant="contentLarge">
        {handbookName}
      </Typography>
      {isHandbookLinkFetching ? (
        <Loader size={22} variant="primary" />
      ) : (
        <button
          onClick={handleHandbookDownload}
          title={t("Download {{ name }}", {
            name: handbookName,
          })}
          className={classes.button}
        >
          <DownloadIcon className={classes.buttonIcon} />
        </button>
      )}
    </li>
  );
};
